import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import MaskedReveal from "@/components/site/MaskedReveal";
import Stars from "@/components/site/Stars";

const HERO_BG =
  "https://images.unsplash.com/photo-1615412998261-e96a5ea9b81e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDF8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwY29uY3JldGUlMjByZXRhaWwlMjBzdG9yZSUyMGludGVyaW9yfGVufDB8fHxibGFja19hbmRfd2hpdGV8MTc4NDE2NTU3NHww&ixlib=rb-4.1.0&q=85";

export default function Hero({ startDelay = 2.2 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.2]);
  const xY = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const titleY = useTransform(scrollYProgress, [0, 1], ["0%", "-15%"]);
  const starsY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden bg-black text-white"
    >
      {/* Deep space base */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_60%,#111_0%,#050505_55%,#000_100%)]" />

      {/* Starfield parallax */}
      <motion.div
        aria-hidden
        style={{ y: starsY }}
        className="absolute inset-0"
      >
        <Stars density={1.4} drift={0.03} />
      </motion.div>

      {/* Concrete boutique bg (low opacity, layered on top of stars) */}
      <motion.div
        aria-hidden
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 mix-blend-lighten"
      >
        <img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover opacity-[0.18] grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black" />
      </motion.div>

      {/* Massive X - kinetic architectural motif */}
      <motion.div
        aria-hidden
        style={{ y: xY }}
        className="absolute inset-0 flex items-center justify-center pointer-events-none select-none"
      >
        <motion.span
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 0.16, scale: 1 }}
          transition={{ duration: 1.6, delay: startDelay, ease: [0.22, 1, 0.36, 1] }}
          className="x-mark text-[62vw] md:text-[46vw] text-white leading-[0.75]"
        >
          X
        </motion.span>
      </motion.div>

      {/* Top labels */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: startDelay + 0.2, duration: 0.9 }}
        className="absolute top-20 md:top-24 left-6 md:left-10 right-6 md:right-10 flex justify-between font-mono text-[10px] md:text-xs uppercase tracking-[0.28em] text-white/70"
      >
        <span data-testid="hero-meta-left">R.I.V.H™ / OFFERING NO. 01</span>
        <span data-testid="hero-meta-right" className="hidden md:inline">
          REAL / INTELLIGENT / VALUABLE / HUMANS
        </span>
      </motion.div>

      {/* Slogan strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: startDelay + 0.5, duration: 1 }}
        className="absolute top-1/2 -translate-y-[calc(50%+34vh)] md:-translate-y-[calc(50%+28vh)] left-1/2 -translate-x-1/2 font-mono text-[10px] md:text-xs uppercase tracking-[0.6em] text-white/60 whitespace-nowrap"
      >
        <span data-testid="hero-slogan">— FIND YOUR PURPOSE —</span>
      </motion.div>

      {/* Main headline */}
      <motion.div
        style={{ y: titleY }}
        className="relative z-10 h-full flex flex-col justify-end px-6 md:px-10 pb-24 md:pb-28"
      >
        <MaskedReveal
          as="h1"
          className="font-display uppercase text-white text-[19vw] md:text-[13vw] leading-[0.82] tracking-[-0.04em]"
          delay={startDelay}
          stagger={0.11}
          lines={["Find your", "purpose."]}
        />

        <div className="mt-10 md:mt-14 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-end">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: startDelay + 0.7, duration: 1 }}
            className="md:col-span-5 text-white/70 text-sm md:text-base max-w-md leading-relaxed"
          >
            R.I.V.H™ — Real. Intelligent. Valuable. Humans. A luxury
            streetwear house cast in cotton, leather and starlight. Not a store
            — a small room in the universe. Walk in.
          </motion.p>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: startDelay + 0.85, duration: 1 }}
            className="md:col-span-4 md:col-start-9 flex md:justify-end"
          >
            <a href="#capsule" className="btn-sharp" data-testid="hero-cta">
              <span>Enter Capsule 01 →</span>
            </a>
          </motion.div>
        </div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: startDelay + 1.4, duration: 0.9 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] uppercase tracking-[0.35em] text-white/50"
      >
        <span className="inline-block animate-pulse">↓ walk into orbit</span>
      </motion.div>
    </section>
  );
}
